import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { BehavioralThumbnailType } from '../../constants-types/BehavioralThumbnail.interface';
import {
    BehavioralFactor,
    ABCD
} from '../../core-data-model/BehavioralFactor.model';
import { Pattern } from '../../core-data-model/Pattern.model';
import { SVGUtilities } from '../../core-utilities/SVG.utility';
import { Util } from '../../core-utilities/Util';

export class BehavioralPatternGraphic {
    private linesToUse: BehavioralFactor[][] = [
        ['A', 'B'],
        ['B', 'C'],
        ['C', 'D']
    ];

    public lines: { x1: number; y1: number; x2: number; y2: number }[];

    public points: { factor: BehavioralFactor; x: number; y: number }[];
    public midpoint: { y1: number; y2: number };

    constructor(
        private pattern: Pattern,
        private width: number,
        private height: number,
        private rightEdgeMax: number,
        private rightEdgeReset: number,
        private leftEdgeMax: number,
        private leftEdgeReset: number
    ) {
        this.points = ABCD.map((factor) => ({
            factor: factor,
            x: this.getXValue(factor),
            y: this.getYValue(factor)
        }));

        this.midpoint = {
            y1: this.getXValue('A'),
            y2: this.getYValue('D')
        };

        this.lines = this.linesToUse.map((line) => ({
            x1: this.getXValue(line[0]),
            y1: this.getYValue(line[0]),
            x2: this.getXValue(line[1]),
            y2: this.getYValue(line[1])
        }));
    }

    getYValue(factor: BehavioralFactor): number {
        return this.getYMultiplier(factor) * this.height;
    }
    private getYMultiplier(factor: BehavioralFactor): number {
        switch (factor) {
            case 'A':
                return 0.2;
            case 'B':
                return 0.4;
            case 'C':
                return 0.6;
            case 'D':
                return 0.8;
        }
    }
    offRightEdge(factor: BehavioralFactor): boolean {
        return this.findXValue(factor) > this.rightEdgeMax;
    }
    offLeftEdge(factor: BehavioralFactor): boolean {
        return this.findXValue(factor) < this.leftEdgeMax;
    }
    getXValue(factor: BehavioralFactor): number {
        if (this.offRightEdge(factor)) {
            return this.rightEdgeReset;
        }
        if (this.offLeftEdge(factor)) {
            return this.leftEdgeReset;
        }
        return this.findXValue(factor);
    }
    private findXValue(factor: BehavioralFactor): number {
        return ((this.pattern[factor] + 3) * this.width) / 6;
    }
}

// Used to determine proportional size of various things based on the width
// Of the image
const originalWidth = 477.42;
const widthToHeightRatio = 105.77 / originalWidth;
const pointRadiusToWidthRatio = 6.49 / originalWidth;
const midpointLineStrokeWidthRatio = 0.6174014197486306 / originalWidth;
const gradientHeightToWidthRatio = 6 / originalWidth;
const factorLineWidthToWidthRatio = 2 / originalWidth;
const triangleHeightToWidthRatio = 8 / originalWidth;
const triangleWidthToWidthRatio = 10 / originalWidth;
const arrowWidthRatio = 4 / originalWidth;
const arrowHeightRatio = 5 / originalWidth;

@Component({
    selector: 'behavioral-thumbnail',
    template: `
        <div
            class="scalingSvgContainer"
            [ngStyle]="{ 'padding-bottom': paddingBottom }"
        >
            <svg
                class="scalingSvg"
                xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink"
                oldviewBox="0 0 477.42 200"
                [attr.viewBox]="viewBox"
            >
                <svg:defs>
                    @for (type of colorMapKeys; track type) {
                        <svg:linearGradient
                            [attr.id]="colorMap[type].gradientId"
                            [attr.x1]="7.63"
                            [attr.x2]="width - 1"
                            [attr.y1]="height - 3"
                            [attr.y2]="height - 3"
                            gradientUnits="userSpaceOnUse"
                        >
                            <svg:stop
                                offset="0"
                                [attr.stop-color]="colorMap[type].start"
                            />
                            <svg:stop
                                offset="1"
                                [attr.stop-color]="colorMap[type].stop"
                            />
                        </svg:linearGradient>
                    }
                </svg:defs>
                <style>
                    line {
                        fill: none;
                        stroke-miterlimit: 10;
                    }
                    .midpoint-line {
                        stroke: #c9cbcc;
                    }
                </style>
                <!-- white background -->
                <svg:rect
                    x="0.5"
                    y="0.5"
                    [attr.width]="width - 1"
                    [attr.height]="height - 5"
                    style="fill:#fff"
                />
                <!-- bordering rect -->
                <svg:path [attr.d]="borderRectPath" style="fill:#c9cbcc" />
                <!-- arrows -->
                @for (points of arrowPoints; track points) {
                    <svg:polygon [attr.points]="points" style="fill:#010101" />
                }
                <!-- midpoint triangle -->
                <svg:polygon [attr.points]="midpointTrianglePoints" />
                <!-- gradient filled rectangle at bottom -->
                <svg:rect
                    [attr.y]="height - 6"
                    [attr.width]="width"
                    [attr.height]="gradientHeight"
                    [ngStyle]="{
                        fill: colorMap[type].gradientUrl
                    }"
                />
                <!-- midpoint line -->
                <svg:line
                    [attr.x1]="width / 2"
                    [attr.x2]="width / 2"
                    [attr.y1]="behavioralPattern.midpoint.y1"
                    [attr.y2]="behavioralPattern.midpoint.y2"
                    class="midpoint-line"
                    [ngStyle]="{
                        'stroke-width': midpointLineStrokeWidth + 'px'
                    }"
                />
                <!-- factor points -->
                @for (point of behavioralPattern.points; track point) {
                    <svg:circle
                        [attr.cx]="point.x"
                        [attr.cy]="point.y"
                        [attr.r]="pointWidth"
                    />
                }
                <!-- factor lines -->
                @for (segment of behavioralPattern.lines; track segment) {
                    <svg:line
                        [attr.x1]="segment.x1"
                        [attr.x2]="segment.x2"
                        oldx1="377.69"
                        oldx2="239.63"
                        [attr.y1]="segment.y1"
                        [attr.y2]="segment.y2"
                        oldy1="20.17"
                        oldy2="40.83"
                        [ngStyle]="{ 'stroke-width': factorLineWidth }"
                        style="stroke:#000;"
                    />
                }
            </svg>
        </div>
    `,
    standalone: false
})
export class BehavioralThumbnailSVGComponent implements OnInit, OnChanges {
    @Input()
    type: BehavioralThumbnailType;
    @Input()
    pattern: Pattern;
    @Input()
    width: number;

    get propertiesList(): string[] {
        return ['type', 'pattern', 'width'];
    }

    SVG: SVGUtilities;
    height: number;
    objectKeys = Object.keys;

    behavioralPattern: BehavioralPatternGraphic;
    midpointTrianglePoints: number[];
    arrowPoints: number[][];
    viewBox: string;
    borderRectPath: string;

    colorMap: {
        [key: string]: {
            start: string;
            stop: string;
            gradientUrl: string;
            gradientId: string;
        };
    } = {};

    colorMapKeys: string[];

    constructor(util: Util) {
        this.SVG = util.svg;

        this.colorMap = {
            // Orange
            [BehavioralThumbnailType.Job]: {
                start: '#ffcca3',
                stop: '#ff773d',
                gradientUrl: this.SVG.getGradientUrl(
                    this.gradientIdForType(BehavioralThumbnailType.Job)
                ),
                gradientId: this.gradientIdForType(BehavioralThumbnailType.Job)
            },
            // Blue
            [BehavioralThumbnailType.Person]: {
                start: '#c2e9ff',
                stop: '#0092c8',
                gradientUrl: this.SVG.getGradientUrl(
                    this.gradientIdForType(BehavioralThumbnailType.Person)
                ),
                gradientId: this.gradientIdForType(
                    BehavioralThumbnailType.Person
                )
            }
        };
        this.colorMapKeys = Object.keys(this.colorMap);
    }

    pointWidth: number;
    midpointLineStrokeWidth: number;
    gradientHeight: number;
    factorLineWidth: number;
    arrowWidth: number;
    arrowHeight: number;
    paddingBottom: string;

    factorOutOfBounds(factor: BehavioralFactor): boolean {
        return (
            this.behavioralPattern.offLeftEdge(factor) ||
            this.behavioralPattern.offRightEdge(factor)
        );
    }

    gradientIdForType(type: BehavioralThumbnailType): string {
        return `linearGradient${type}`;
    }

    ngOnInit() {
        this.renderThumbnail();
    }

    ngOnChanges() {
        this.renderThumbnail();
    }

    renderThumbnail() {
        if (!this.pattern) {
            throw new Error('Behavioral thumbnail initialized with no pattern');
        }
        if (!this.width) {
            this.width = originalWidth;
        }

        this.pointWidth = pointRadiusToWidthRatio * this.width;
        this.midpointLineStrokeWidth =
            midpointLineStrokeWidthRatio * this.width;
        this.gradientHeight = gradientHeightToWidthRatio * this.width;
        this.factorLineWidth = factorLineWidthToWidthRatio * this.width;
        this.arrowWidth = arrowWidthRatio * this.width;
        this.arrowHeight = arrowHeightRatio * this.width;
        this.height = this.width * widthToHeightRatio;
        this.paddingBottom = this.SVG.getPaddingBottom(this.width, this.height);
        const midpoint = this.width / 2;

        const rightEdgeMax = this.width - this.pointWidth;
        const rightEdgeReset = this.width - this.pointWidth * 2;
        const leftEdgeMax = this.pointWidth;
        const leftEdgeReset = this.pointWidth * 2;

        this.behavioralPattern = new BehavioralPatternGraphic(
            this.pattern,
            this.width,
            this.height,
            rightEdgeMax,
            rightEdgeReset,
            leftEdgeMax,
            leftEdgeReset
        );

        const triangleWidth =
            this.gradientHeight + triangleWidthToWidthRatio * this.width;
        const triangleHeight =
            this.gradientHeight + triangleHeightToWidthRatio * this.width;
        this.midpointTrianglePoints = [
            midpoint,
            this.height - triangleHeight,

            midpoint + triangleWidth / 2,
            this.height,

            midpoint - triangleWidth / 2,
            this.height
        ];
        this.viewBox = `0 0 ${this.width} ${this.height}`;

        this.arrowPoints = [];
        // Generate arrow points for each factor
        this.behavioralPattern.points.forEach((point) => {
            const offLeft = this.behavioralPattern.offLeftEdge(point.factor);
            const offRight = this.behavioralPattern.offRightEdge(point.factor);
            if (!offRight && !offLeft) {
                return;
            }
            const xValue = point.x;
            const yValue = point.y;
            const arrowPoints = offRight
                ? [
                      xValue + this.pointWidth + 2 + this.arrowWidth,
                      yValue,
                      xValue + this.pointWidth + 2,
                      yValue + this.arrowHeight / 2,
                      xValue + this.pointWidth + 2,
                      yValue - this.arrowHeight / 2
                  ]
                : [
                      xValue - this.pointWidth - 2 - this.arrowWidth,
                      yValue,
                      xValue - this.pointWidth - 2,
                      yValue - this.arrowHeight / 2,
                      xValue - this.pointWidth - 2,
                      yValue + this.arrowHeight / 2
                  ];
            this.arrowPoints.push(arrowPoints);
        });

        // M476.42,1V99.85H1V1H476.42m1-1H0V100.85H477.42V0Z;
        this.borderRectPath =
            `M${this.width - 1},1V${this.height - 5}H1V1H${this.width - 1}` +
            `m1-1H0V${this.height - 5}H${this.width}V0Z`;
    }
}
